import type {
    ILabelModel,
    ILabelModelPaginationResult,
    IPagination,
    IValueModelPaginatedResult,
    PermissionType,
} from '@experiences/interfaces';

import type { ITagLabelDto } from '../../component/tags/addedit/labels/AddEditTenantTagsLabelsViewModel';
import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests.default';
import {
    cleanLabelTagResponse,
    cleanValueTagResponse,
    DEFAULT_SEARCH_FILTERS,
    mapObjectId,
    mapPagination,
} from './TagsUtil';

export const tagsUrl = '/api/orchestrator/tags';

export async function getPermissions(
    accountLogicalName: string,
    tenantName: string | undefined,
    _selectedAccountId: string,
) {
    return await get<PermissionType[]>(`${tagsUrl}/${accountLogicalName}/${tenantName}/permissions`);
}

export async function getLabels(
    pagination: IPagination,
    accountLogicalName: string,
    tenantName: string | undefined,
    _selectedAccountId: string,
) {
    const response = await get<ILabelModelPaginationResult>(`${tagsUrl}/${accountLogicalName}/${tenantName}`, {
        urlParams: {
            ...DEFAULT_SEARCH_FILTERS,
            ...mapPagination(pagination),
            type: 'Label',
        },
    });

    return cleanLabelTagResponse(response);
}

export async function getKeyValues(
    pagination: IPagination,
    accountLogicalName: string,
    tenantName: string | undefined,
    _selectedAccountId: string,
) {
    const response = await get<ILabelModelPaginationResult>(`${tagsUrl}/${accountLogicalName}/${tenantName}`, {
        urlParams: {
            ...DEFAULT_SEARCH_FILTERS,
            ...mapPagination(pagination),
            type: 'KeyValue',
        },
    });

    return cleanLabelTagResponse(response);
}

export async function getLabel(
    _url: string,
    accountLogicalName: string,
    tenantName: string | undefined,
    id: string,
    _selectedAccountId: string,
) {
    const response = await get<ILabelModel>(`${tagsUrl}/${accountLogicalName}/${tenantName}/${id}`);
    return mapObjectId(response);
}

export async function createLabel(
    accountLogicalName: string,
    tenantName: string | undefined,
    payload: ITagLabelDto,
    _selectedAccountId: string,
) {
    payload.type = 'Label';
    return post(`${tagsUrl}/${accountLogicalName}/${tenantName}`, { body: payload });
}

export async function createKeyValue(
    accountLogicalName: string,
    tenantName: string | undefined,
    payload: ITagLabelDto,
    _selectedAccountId: string,
) {
    payload.type = 'KeyValue';
    return post(`${tagsUrl}/${accountLogicalName}/${tenantName}`, { body: payload });
}

export async function updateLabel(
    accountLogicalName: string,
    tenantName: string | undefined,
    id: string | undefined,
    payload: ITagLabelDto,
    _selectedAccountId: string,
) {
    return put(`${tagsUrl}/${accountLogicalName}/${tenantName}/${id}`, { body: payload });
}

export async function deleteLabel(accountLogicalName: string, tenantName: string | undefined, keys: string[], _selectedAccountId: string) {
    return axiosDelete(`${tagsUrl}/${accountLogicalName}/${tenantName}`,
        {
            urlParams: {
                keys,
                type: 'Label',
            },
        },

    );
}

export async function deleteKeyValue(
    accountLogicalName: string,
    tenantName: string | undefined,
    keys: string[],
    _selectedAccountId: string,
) {
    return axiosDelete(`${tagsUrl}/${accountLogicalName}/${tenantName}`,
        {
            urlParams: {
                keys,
                type: 'KeyValue',
            },
        },
    );
}

export async function getValuesForKey(
    pagination: IPagination,
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    _selectedAccountId: string,
) {
    const response = await get<IValueModelPaginatedResult>(`${tagsUrl}/${accountLogicalName}/${tenantName}/${key}/values`, {
        urlParams: {
            ...DEFAULT_SEARCH_FILTERS,
            ...mapPagination(pagination),
        },
    });
    return cleanValueTagResponse(response);
}

export async function createValuesForKey(
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    payload: { labelKey: string; values: string[]; accountKey: string; tenantKey: string },
    _selectedAccountId: string,
) {
    return post(`${tagsUrl}/${accountLogicalName}/${tenantName}/${key}/values`, { body: payload });
}

export async function deleteValueFromKey(
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    valueKey: string,
    selectedAccountId: string,
) {
    return axiosDelete(`${tagsUrl}/${accountLogicalName}/${tenantName}/${key}/values/${valueKey}`);
}

export async function deleteValuesFromKey(
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    valueKeys: string[],
    _selectedAccountId: string,
) {
    return axiosDelete(`${tagsUrl}/${accountLogicalName}/${tenantName}/${key}/values`,
        { urlParams: { valueKeys } },
    );
}
